import { QuoteStatus, QuoteType, MultiPolicyQuoteStatus, TQuoteApiResponse } from '@models/quotes';
import { AlertStatus } from '@models/alert';
import {
  MultiPolicyQuoteInternalAlertInfo,
  MultiPolicyQuoteExternalAlertInfo,
  alertQuoteStatuses,
  activeQuoteStatuses,
  QuoteStatusAlertInfo,
  QuoteReadOnlyAlertInfo,
  underlyingCoverageBindingRequirementDefaultMessage,
  isUnderlyingCoverageBindingRequirementUnmet,
  Action,
} from '@pages/QuoteDetails/constants/QuoteDetails.constants';

type IQuoteAlert = {
  /** The quote message to display */
  message: string;
  /** The severity of the alert  */
  severity: AlertStatus;
  /** optional call to action text to display */
  ctaText?: string;
  /** optional call to action link url */
  ctaUrl?: string;
  /** optional click handler instead of a link */
  ctaAction?: () => Action;
  ctaDisabled?: boolean;
  ctaHoverText?: string;
};

export const getQuotePolicyAlerts = ({
  quote,
  isReadOnlyStatus,
  isInternalUser,
  isUnderwriter,
  isLIOContractor,
  isJrOps,
  isBroker,
  isSrOps,
  isPolicyMarkedForNonrenewal,
  hasFormConflicts = false,
}: {
  quote: TQuoteApiResponse;
  isReadOnlyStatus: boolean;
  isInternalUser: boolean;
  isUnderwriter: boolean;
  isLIOContractor: boolean;
  isJrOps: boolean;
  isSrOps: boolean;
  isBroker: boolean;
  isPolicyMarkedForNonrenewal?: boolean;
  hasFormConflicts?: boolean;
}): IQuoteAlert[] | null => {
  const alerts: IQuoteAlert[] = [];

  // Persistent banner whenever the LSIC issuing-company override is applied. The API
  // returns the override as a nested `issuingWritingCompany` relation — there is no scalar
  // id on a quote read.
  if (quote?.issuingWritingCompany?.id) {
    alerts.push({
      severity: AlertStatus.Info,
      message:
        'This quote is issued by LSIC (LIO Specialty Insurance Company). It continues to rate, apply ' +
        'coverages, and select forms as LIC (LIO Insurance Company).',
    });
  }

  const quoteStatusAlertName = quote?.status?.name ?? '';

  const showFailedToBindMultiPolicyQuote =
    quote?.multiPolicyQuoteDestinations?.some(
      (mp: any) =>
        mp.destinationQuote &&
        mp.destinationQuote.id !== quote?.id &&
        mp.destinationQuote?.status?.name !== QuoteStatus.Bound.name,
    ) ||
    (quote?.multiPolicyQuoteSource &&
      quote?.multiPolicyQuoteSource?.sourceQuote?.id !== quote?.id &&
      quote?.multiPolicyQuoteSource?.sourceQuote?.status?.name !== QuoteStatus.Bound.name);

  const showQuoteStatusAlert =
    quote !== undefined && isInternalUser && alertQuoteStatuses.includes(quoteStatusAlertName);

  const showQuoteReadOnlyAlert = isReadOnlyStatus && !isInternalUser;

  const selfBound = quote?.status?.name === QuoteStatus.Bound.name;

  const hasRolesForReleaseBanner = isUnderwriter || isLIOContractor || isBroker || isSrOps || isJrOps; // intentionally NOT internal

  const showRestrictedRenewalAlert =
    quote?.hasInternalOnlyAccessRestriction && activeQuoteStatuses.includes(quoteStatusAlertName);

  const hideCanceledReactivationAlertForMarkedNonrenewal =
    quote?.quoteType === QuoteType.Renewal &&
    Boolean(isPolicyMarkedForNonrenewal) &&
    quoteStatusAlertName === QuoteStatus.Canceled.name;

  if (showFailedToBindMultiPolicyQuote) {
    alerts.push(
      isInternalUser
        ? MultiPolicyQuoteInternalAlertInfo[
            selfBound
              ? MultiPolicyQuoteStatus.QuoteBoundRelatedQuoteFailedBind
              : MultiPolicyQuoteStatus.QuoteFailedBindRelatedQuoteFailedBind
          ]
        : MultiPolicyQuoteExternalAlertInfo[
            selfBound
              ? MultiPolicyQuoteStatus.QuoteBoundRelatedQuoteFailedBind
              : MultiPolicyQuoteStatus.QuoteFailedBindRelatedQuoteFailedBind
          ],
    );
  }
  if (showQuoteStatusAlert) {
    if (
      [QuoteStatus.Rejected.name, QuoteStatus.Declined.name].includes(quote.status.name) &&
      isInternalUser &&
      !isPolicyMarkedForNonrenewal
    ) {
      alerts.push({
        message: QuoteStatusAlertInfo[quoteStatusAlertName]?.message ?? '',
        severity: QuoteStatusAlertInfo[quoteStatusAlertName]?.severity ?? AlertStatus.Info,
        ctaText: 'Reactivate Quote',
        ctaAction: () => {
          return Action.ReactivateDeclinedRejectedQuote;
        },
      });
    } else if (hideCanceledReactivationAlertForMarkedNonrenewal) {
      alerts.push({
        message: QuoteReadOnlyAlertInfo[quoteStatusAlertName]?.message ?? 'This quote has been cancelled.',
        severity: QuoteStatusAlertInfo[quoteStatusAlertName]?.severity ?? AlertStatus.Info,
      });
    } else {
      alerts.push(QuoteStatusAlertInfo[quoteStatusAlertName]);
    }
  }
  if (showQuoteReadOnlyAlert) {
    alerts.push(QuoteReadOnlyAlertInfo[quoteStatusAlertName]);
  }

  if (showRestrictedRenewalAlert) {
    alerts.push({
      severity: AlertStatus.Info,
      message: `This ${
        quote.quoteType === 'Renewal' ? 'renewal' : 'quote'
      } is currently restricted to LIO, external users do not have access`,
    });
  }

  if (quote?.hasUnderwritingLock) {
    alerts.push({
      severity: AlertStatus.Warning,
      message:
        isUnderwriter || isLIOContractor
          ? 'The quote is currently locked'
          : 'The quote has been locked by an underwriter',
    });
  }

  const underlyingCoverageBindingRequirement = quote?.underlyingCoverageBindingRequirement;
  if (isUnderlyingCoverageBindingRequirementUnmet(underlyingCoverageBindingRequirement)) {
    const { quoteId: glQuoteId, policyId: glPolicyId, reason } = underlyingCoverageBindingRequirement;

    alerts.push({
      severity: AlertStatus.Warning,
      message: reason || underlyingCoverageBindingRequirementDefaultMessage,
      ctaText: glQuoteId ? 'View GL Quote' : glPolicyId ? 'View GL Policy' : undefined,
      ctaUrl: glQuoteId ? `/quote/${glQuoteId}` : glPolicyId ? `/policy/${glPolicyId}` : undefined,
    });
  }

  if (isInternalUser && hasFormConflicts) {
    alerts.push({
      severity: AlertStatus.Warning,
      message: 'This quote has conflicting forms on it, you must remove all conflicts before binding.',
    });
  }

  if (quote?.hasDuplicateQuotes) {
    alerts.push({
      severity: AlertStatus.Warning,
      message: 'This quote is flagged as a duplicate',
      ctaText: 'View Duplicates',
      ctaAction: () => {
        return Action.ViewDuplicateQuotes;
      },
    });
  }

  if (quote?.referredToBroker && !quote?.releasedToPolicyHolder && hasRolesForReleaseBanner) {
    const buttonDisabled = ![QuoteStatus.Underwritten.id, QuoteStatus.ReadyToBind.id].includes(quote?.status?.id);

    alerts.push({
      severity: AlertStatus.Info,
      message: 'This quote is currently blocked from Policyholders and will need to be released before binding.',
      ctaText: 'Release to Policyholder',
      ctaDisabled: buttonDisabled,
      ctaHoverText: buttonDisabled
        ? 'Quote must be released from Underwriting and ready to bind before it can be released to the policyholder'
        : undefined,
      ctaAction: () => {
        return Action.ReleaseQuoteToPolicyHolder;
      },
    });
  }

  return alerts?.length > 0 ? alerts : null;
};
